// eslint-disable-next-line max-classes-per-file
export interface Type<T = object> extends Function {
  new (...args: any[]): T;
  deps?: any[];
}

export interface AbstractType<T> extends Function {
  prototype: T;
}

export interface TypeProvider extends Type<any> {
}

export interface ValueProvider {
  provide: any;
  useValue: any;
  multi?: boolean;
}

export interface ClassProvider {
  provide: any;
  useClass: Type<any>;
  multi?: boolean;
}

export interface ExistingProvider {
  provide: any;
  useExisting: AbstractType<any> | Type<any>;
  multi?: boolean;
}

export type Provider = TypeProvider | ValueProvider | ClassProvider | ExistingProvider;

export class InjectionToken<T = any> {
  // eslint-disable-next-line no-empty-function
  constructor(public desc: string) { }

  toString() {
    return this.desc;
  }
}

export class Injector {
  resolvedProviders = new Map<any, any>();

  // eslint-disable-next-line no-empty-function
  constructor(public providers: Provider[], public parentInjector?: Injector) {
    this.resolvedProviders.set(Injector, this);
  }

  get<T = any>(Service: AbstractType<T> | Type<T> | InjectionToken<T>, notFoundValue?: T): T {
    const providerIndex  = this.providers.findIndex((p) => {
      if ('provide' in p) {
        return p.provide === Service;
      }

      return p === Service;
    });

    if (providerIndex < 0) {
      if (!this.parentInjector) {
        if (notFoundValue) {
          return notFoundValue;
        }

        throw new Error('Provider not found!');
      }

      return this.parentInjector.get(Service);
    }

    const serviceProvider = this.providers[providerIndex];

    let resolvedService = this.resolvedProviders.get(Service);

    if (resolvedService) {
      return resolvedService;
    }

    if ('useValue' in serviceProvider) {
      resolvedService = serviceProvider.useValue;
      this.resolvedProviders.set(Service, resolvedService);

      return resolvedService;
    }

    let ServiceToResolve = Service as Type<T>;

    if ('useExisting' in serviceProvider) {
      resolvedService = this.get(serviceProvider.useExisting);
      this.resolvedProviders.set(Service, resolvedService);

      return resolvedService;
    }

    if ('useClass' in serviceProvider) {
      ServiceToResolve = serviceProvider.useClass;
    }

    if (!ServiceToResolve.deps?.length) {
      resolvedService = new ServiceToResolve();
      this.resolvedProviders.set(Service, resolvedService);

      return resolvedService;
    }

    const resolvedDeps = ServiceToResolve.deps.map((dep) => {
      let resolvedDep = this.resolvedProviders.get(dep);

      if (!resolvedDep) {
        resolvedDep = this.get(dep);
      }

      return resolvedDep;
    });

    resolvedService = new ServiceToResolve(...resolvedDeps);
    this.resolvedProviders.set(Service, resolvedService);

    return resolvedService;
  }
}

export class ReflectiveInjector {
  static resolveAndCreate(providers: Provider[], parent?: Injector) {
    return new Injector(providers, parent);
  }
}
